import { errCode } from './apiError';

/** Wire `error.code` values; case is fixed by the API (SCREAMING_CASE and snake_case). */
export const CHAT_ERROR_CODE = {
  CONFLICT: 'CONFLICT_ERROR',
  AUTHENTICATION: 'AUTHENTICATION_ERROR',
  AUTHORIZATION: 'AUTHORIZATION_ERROR',
};

// Someone else claimed the thread first. A normal outcome of a shared pool, not a failure.
export const isClaimConflict = (error) =>
  error?.response?.status === 409 || errCode(error) === CHAT_ERROR_CODE.CONFLICT;

// An invalid JWT answers 403 (code-less from DRF too). AUTHORIZATION_ERROR is a role or
// ownership refusal that signing in again cannot fix, so it is not re-auth.
export const isReauthRequired = (error) => {
  if (error?.response?.status !== 403) return false;
  const code = errCode(error);
  return !code || code === CHAT_ERROR_CODE.AUTHENTICATION;
};

// Role or ownership refusal: the caller is signed in, just not allowed here.
export const isNotPermitted = (error) =>
  error?.response?.status === 403 && errCode(error) === CHAT_ERROR_CODE.AUTHORIZATION;

const SUPPORT_HISTORY_HINTS = {
  unsupported_queue: 'Coaching history is not part of the support surface.',
  malformed_id: 'That customer id is not a valid reference.',
};

// The documented 400s carry a fixable cause; a bare "failed to load" hides it.
// There is no per-customer ownership check - the role is the only gate.
export const supportHistoryHint = (error) => {
  if (isNotPermitted(error)) return 'Support history is open to support agents only.';
  return SUPPORT_HISTORY_HINTS[errCode(error)] || null;
};

// Sends dedupe on (conversation_id, client_msg_id); a retry must reuse the same id.
export const newClientMsgId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // crypto.randomUUID needs a secure context; plain-http dev origins fall back.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) =>
    c === 'x'
      ? Math.floor(Math.random() * 16).toString(16)
      : // the variant nibble is one of 8, 9, a, b
        (Math.floor(Math.random() * 4) + 8).toString(16)
  );
};

// WebSocket can't carry an Authorization header, so the JWT rides as a query param.
const socketUrl = (path) => {
  const root =
    process.env.REACT_APP_API_ROOT_URL ||
    new URL(
      process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1',
      window.location.origin
    ).origin;
  const url = new URL(path, root.endsWith('/') ? root : `${root}/`);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  const token = localStorage.getItem('access_token');
  if (token) url.searchParams.set('token', token);
  return url.toString();
};

export const agentSocketUrl = () => socketUrl('ws/v1/chat/agent/');

export const conversationSocketUrl = (conversationId) =>
  socketUrl(`ws/v1/chat/conversations/${conversationId}/`);

// A close naming another thread is a stale transfer or requeue; no id (back button) applies.
export const closeAppliesTo = (closedId, threadId) =>
  !closedId || !threadId || closedId === threadId;
